import type {
  DubDirection,
  DubDirectionMechanism,
  DubKnobDeclarationDTO,
  DubModelCapabilityDTO,
} from '@src/repositories/DubbingRepository';

/**
 * PLAT-12023 — the WORDS a person reads for each control, kept beside the
 * ranges rather than in the component that draws them: the field never looks
 * a control up by name or branches on a provider. Keyed on the provider's own
 * control name, normalized the way {@link knobKey} normalizes it, because the
 * same control name means the same thing on every model that declares it.
 *
 * The backend's capability catalog carries the type and range only; carrying
 * these words there too is PLAT-12030. When it does, this table becomes the
 * fallback for a row that does not.
 */
export interface KnobPresentation {
  /** Title Case: "Stability", "Similarity", "Style Exaggeration". */
  label: string;
  /** The two ends of a number control's slider. Sentence case. */
  ends?: { low: string; high: string };
  /** One sentence on what moving it does. */
  effect: string;
}

const KNOB_PRESENTATION: Record<string, KnobPresentation> = {
  stability: {
    label: 'Stability',
    ends: { low: 'More variable', high: 'More stable' },
    effect:
      'Lower is more expressive and varies from line to line; higher is steadier and more predictable.',
  },
  similarity_boost: {
    label: 'Similarity',
    ends: { low: 'Low', high: 'High' },
    effect:
      'How closely the voice sticks to the original speaker’s sound; high can carry over noise from the source recording.',
  },
  style: {
    label: 'Style Exaggeration',
    ends: { low: 'None', high: 'Exaggerated' },
    effect:
      'How much the voice leans into its speaking style; higher is more dramatic and slower to generate.',
  },
  speed: {
    label: 'Speed',
    ends: { low: 'Slower', high: 'Faster' },
    effect: 'How fast the lines are read; 1 is the voice’s natural pace.',
  },
  use_speaker_boost: {
    label: 'Speaker Boost',
    effect:
      'Pushes the voice closer to the original speaker’s likeness; slightly slower to generate.',
  },
};

/** A control name as the tables here key it: `Similarity-Boost` → `similarity_boost`. */
const knobKey = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

/**
 * A control's name as the provider writes it (`similarity_boost`) turned into
 * words a person reads ("Similarity boost"), for a control nobody has written
 * up.
 */
export function knobCaption(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The words for one control. Never a bare number: a control with no entry in
 * the table still gets a label from its name, its two ends from the declared
 * range, and a sentence that says the range and admits the rest is unknown —
 * so nothing drawn is ever a number with no words around it (PLAT-12023).
 */
export function knobPresentation(knob: DirectionKnob): KnobPresentation {
  // The route's words win when it carries them (PLAT-12030); the table here
  // is the fallback for a row that does not.
  const known = knob.presentation ?? KNOB_PRESENTATION[knobKey(knob.name)];
  if (known) return known;
  if (knob.type === 'number') {
    return {
      label: knobCaption(knob.name),
      ends: { low: String(knob.min), high: String(knob.max) },
      effect: `Accepted by these voices between ${knob.min} and ${knob.max}; what it changes hasn’t been described yet.`,
    };
  }
  return {
    label: knobCaption(knob.name),
    effect:
      'Accepted by these voices; what it changes hasn’t been described yet.',
  };
}

/**
 * PLAT-12023 — the delivery markers a tag-reading model documents, grouped by
 * what they change. The list says what may be OFFERED; what the provider
 * accepts is still the provider's answer (settled 2026-09-20), so a marker
 * outside the list is pointed at, never refused — see {@link unknownMarkers}.
 *
 * Keyed on the provider's model id, normalized like a control name. The
 * backend's catalog carries none of this yet (PLAT-12030).
 */
export type MarkerKind = 'emotion' | 'pacing' | 'delivery';

export interface MarkerDeclaration {
  /** The marker as written inside the brackets: `whispers`. */
  tag: string;
  kind: MarkerKind;
}

const marker = (kind: MarkerKind, tags: string): MarkerDeclaration[] =>
  tags.split(',').map(tag => ({ tag: tag.trim(), kind }));

// ElevenLabs' v3 audio-tag vocabulary, from its prompting guide (the one the
// catalog's notes cite, read 2026-09-18). Shared by every ElevenLabs model that
// reads tags, which the guide says follow one convention.
const ELEVEN_V3_MARKERS: MarkerDeclaration[] = [
  ...marker(
    'emotion',
    'excited, happy, sad, angry, nervous, curious, sarcastic, mischievously, playfully',
  ),
  ...marker(
    'pacing',
    'pause, short pause, long pause, rushed, slowly, drawn out, hesitates, stammers',
  ),
  ...marker(
    'delivery',
    'whispers, shouts, laughs, laughs harder, chuckles, giggles, sighs, exhales, gasps, clears throat, crying, starts laughing',
  ),
];

const MARKER_VOCABULARY: Record<string, MarkerDeclaration[]> = {
  eleven_v3: ELEVEN_V3_MARKERS,
  eleven_v4: ELEVEN_V3_MARKERS,
};

/** Order the editor lists the kinds in. */
export const MARKER_KINDS: readonly { kind: MarkerKind; label: string }[] = [
  { kind: 'emotion', label: 'Emotion' },
  { kind: 'pacing', label: 'Pacing' },
  { kind: 'delivery', label: 'Delivery' },
];

/** A marker as it is compared: lower case, one space between words. */
const markerKey = (tag: string): string =>
  tag.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The bracketed markers in a marked-up line that are NOT in the list, in the
 * order they appear, each once, as written. Empty when there is no list, since
 * with nothing to compare against nothing can honestly be called unknown.
 */
export function unknownMarkers(
  text: string,
  markers: readonly MarkerDeclaration[],
): string[] {
  if (markers.length === 0) return [];
  const known = new Set(markers.map(m => markerKey(m.tag)));
  const seen = new Set<string>();
  const unknown: string[] = [];
  for (const match of text.matchAll(/\[([^\]]*)\]/g)) {
    const inside = match[1] ?? '';
    const key = markerKey(inside);
    if (!key || known.has(key) || seen.has(key)) continue;
    seen.add(key);
    unknown.push(inside.trim());
  }
  return unknown;
}

/**
 * PLAT-12023 — the provider's own guidance for directing a model, linked from
 * the direction card as "Best Practices". Per model, not per provider: the
 * same provider's v2 (settings) and v3 (tags) are documented on different
 * pages.
 */
export interface DirectionGuidance {
  /** The model's readable label, to tell two links apart. */
  modelLabel: string;
  url: string;
}

const MODEL_GUIDANCE: Record<string, string> = {
  eleven_v3: 'https://elevenlabs.io/docs/best-practices/prompting/eleven-v3',
  eleven_v4: 'https://elevenlabs.io/docs/best-practices/prompting/eleven-v3',
  eleven_multilingual_v2:
    'https://elevenlabs.io/docs/product-guides/playground/text-to-speech',
  gpt_4o_mini_tts: 'https://platform.openai.com/docs/guides/text-to-speech',
};

/**
 * PLAT-11385 — what the people using a dub run may be offered as a direction,
 * decided by what each voice's text-to-speech model declares it can take, and
 * by nothing else. Pure: no React, no MobX, no IO. The hook that fetches the
 * declarations (`useDubDirectionCapability`) gathers the inputs; the popover
 * renders the answer.
 *
 * Two words used throughout:
 * - a DIRECTION is a statement of how the lines should be delivered;
 * - a MECHANISM is the way a model can receive one — `native` (a written note
 *   the model reads), `settings` (numeric knobs) or `tags` (markup inside the
 *   line's own text).
 *
 * All three now exist as controls, so this module answers, for each of them:
 * may it be offered to this run at all, and if not, whose voice stops it. For
 * the numeric controls it also answers WHICH controls may be drawn and between
 * which values, folded across every model the run will use — so a value the
 * panel allows is a value every voice in the run accepts.
 */

/** One voice a run will speak with, as the popover knows it before the run. */
export interface DirectionVoice {
  /** The speaker this voice belongs to, named as the popover names them. */
  speakerName: string;
  /** The `ai_voice` record the run will use. */
  voiceId: string;
  /**
   * The provider's model string behind that voice (`ai_voice.model.modelId`).
   * Absent when the catalog record names no model — which is not the same as a
   * model that takes nothing, and is reported apart from it.
   */
  modelId?: string;
  /** That model's readable label (`ai_voice.model.displayName`). */
  modelLabel?: string;
}

/**
 * What one voice can be asked for:
 * - `note` — a written direction, the only control that exists today;
 * - `settings` — numeric voice settings, no written note;
 * - `tags` — markup inside the line, no written note;
 * - `none` — the model declares no way to be directed;
 * - `unknown` — the voice names no model, or the capability read returned no
 *   row for it. Treated exactly like `none` for what is offered, because
 *   offering a control the model may refuse is the thing this prevents.
 */
export type DirectionVerdict =
  | 'note'
  | 'settings'
  | 'tags'
  | 'none'
  | 'unknown';

/** One row of the "how this voice can be directed" hint. */
export interface DirectionVoiceSummary {
  speakerName: string;
  modelLabel: string | null;
  verdict: DirectionVerdict;
  mechanisms: DubDirectionMechanism[];
}

/** The whole run's answer. */
export interface DirectionSupport {
  /** One row per speaker in the run, in the order they were given. */
  voices: DirectionVoiceSummary[];
  /**
   * True only when EVERY voice in the run takes a written note. A run is
   * all-or-nothing: a note that reaches some lines and not others is exactly
   * the half-directed dub the design refuses (settled in issue 09 — a span
   * covering a voice that cannot take a direction refuses the whole run and
   * names the speaker that blocked it).
   */
  noteAccepted: boolean;
  /** The speakers whose voice stops a written note, in run order. */
  blockedBy: string[];
  /** True when at least one voice declares some way of being directed. */
  anyDirectable: boolean;
  /**
   * PLAT-11797 — true only when EVERY voice in the run takes the numeric and
   * on/off controls. All-or-nothing, for the same reason a written note is: a
   * run carries ONE direction, and the backend refuses the whole run before
   * making any audio if a single voice cannot take it.
   */
  settingsAccepted: boolean;
  /** The speakers whose voice stops the controls panel, in run order. */
  settingsBlockedBy: string[];
  /**
   * The controls the panel may draw: only those EVERY model in the run
   * declares, at the narrowest range all of them accept. A control one model
   * has and another does not is left out, because the one direction sent would
   * be refused for the model that does not have it.
   *
   * Empty when the panel has nothing it can honestly draw.
   */
  knobs: DirectionKnob[];
  /**
   * Controls every model in the run names but none of them declares limits
   * for. Named to the person rather than drawn, because drawing one means
   * inventing a range the backend would then refuse.
   */
  undeclaredKnobs: string[];
  /**
   * PLAT-11798 — true only when EVERY voice in the run takes delivery markers
   * written into the line's own text.
   */
  tagsAccepted: boolean;
  /** The speakers whose voice stops the markers editor, in run order. */
  tagsBlockedBy: string[];
  /**
   * PLAT-12023 — the delivery markers the editor may offer: only those EVERY
   * tag-reading model in the run documents, in the first model's order. Empty
   * when any of them has no documented list, and then the editor offers no
   * list and flags nothing.
   */
  markers: MarkerDeclaration[];
  /** PLAT-12023 — the provider guidance pages for the run's models, distinct. */
  guidance: DirectionGuidance[];
}

/**
 * One control the panel can draw, folded across every model in the run.
 *
 * A NUMBER control carries the lowest and highest value every model in the run
 * accepts, and the starting value to use when there is no previous run. An
 * ON/OFF control carries only that starting value.
 */
export type DirectionKnob =
  | {
      name: string;
      type: 'number';
      min: number;
      max: number;
      default: number;
      /** The words the route declared for it, when it did (PLAT-12030). */
      presentation?: KnobPresentation;
    }
  | {
      name: string;
      type: 'boolean';
      default: boolean;
      presentation?: KnobPresentation;
    };

/** The distinct model strings to ask the capability route about. */
export function directionModelIds(voices: readonly DirectionVoice[]): string[] {
  const ids = new Set<string>();
  for (const voice of voices) {
    if (voice.modelId) ids.add(voice.modelId);
  }
  return Array.from(ids).sort();
}

function verdictFor(
  mechanisms: DubDirectionMechanism[],
): Exclude<DirectionVerdict, 'unknown'> {
  if (mechanisms.includes('native')) return 'note';
  if (mechanisms.includes('settings')) return 'settings';
  if (mechanisms.includes('tags')) return 'tags';
  return 'none';
}

/**
 * Fold the run's voices together with the models' declarations into the one
 * answer the popover needs.
 *
 * `capabilities` is the capability route's answer as it came back. A model the
 * answer does not mention is `unknown`, never "supported": the route may drop a
 * row it cannot describe, and a missing row must not read as permission.
 *
 * The same speaker with the same voice twice (two transcript labels behind one
 * speaker) is one row, so the hint does not repeat itself.
 */
export function summarizeDirectionSupport(
  voices: readonly DirectionVoice[],
  capabilities: readonly DubModelCapabilityDTO[],
): DirectionSupport {
  const byModelId = new Map(capabilities.map(row => [row.modelId, row]));
  const seen = new Set<string>();
  const summaries: DirectionVoiceSummary[] = [];

  for (const voice of voices) {
    const key = `${voice.speakerName}\u0000${voice.voiceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const row = voice.modelId ? byModelId.get(voice.modelId) : undefined;
    const mechanisms = row?.mechanisms ?? [];
    summaries.push({
      speakerName: voice.speakerName,
      modelLabel: voice.modelLabel ?? row?.modelId ?? null,
      // A row the catalog marks undeclared says nothing about the model, the
      // same as no row at all: unknown, never "takes nothing".
      verdict: row?.declared ? verdictFor(mechanisms) : 'unknown',
      mechanisms,
    });
  }

  const blockedBy = summaries
    .filter(summary => summary.verdict !== 'note')
    .map(summary => summary.speakerName);
  // A voice may take several forms at once — the ElevenLabs v3 model takes both
  // the numeric controls and the markers — so these are read off the declared
  // set, not off the single `verdict` the hint line uses.
  const settingsBlockedBy = summaries
    .filter(summary => !summary.mechanisms.includes('settings'))
    .map(summary => summary.speakerName);
  const tagsBlockedBy = summaries
    .filter(summary => !summary.mechanisms.includes('tags'))
    .map(summary => summary.speakerName);
  const settingsAccepted =
    summaries.length > 0 && settingsBlockedBy.length === 0;

  const usedRows = distinctRowsFor(voices, byModelId);
  const { knobs, undeclaredKnobs } = settingsAccepted
    ? foldKnobs(usedRows)
    : { knobs: [], undeclaredKnobs: [] };
  const tagsAccepted = summaries.length > 0 && tagsBlockedBy.length === 0;
  const markers = tagsAccepted ? foldMarkers(usedRows) : [];
  const guidance = foldGuidance(voices, byModelId);

  return {
    voices: summaries,
    noteAccepted: summaries.length > 0 && blockedBy.length === 0,
    blockedBy,
    anyDirectable: summaries.some(summary =>
      ['note', 'settings', 'tags'].includes(summary.verdict),
    ),
    settingsAccepted,
    settingsBlockedBy,
    knobs,
    undeclaredKnobs,
    tagsAccepted,
    tagsBlockedBy,
    markers,
    guidance,
  };
}

/**
 * The markers every tag-reading model in the run documents: one marked-up line
 * reaches every voice, so a marker only one model knows is left out, the same
 * way a control only one model has is. A model with no documented list empties
 * the answer — nothing can be offered as "read by every voice" when one voice's
 * list is unknown.
 */
function foldMarkers(
  rows: readonly DubModelCapabilityDTO[],
): MarkerDeclaration[] {
  // The route's list wins when it carries one (PLAT-12030); the table here
  // stands in for a row that does not.
  const lists = rows.map(
    row => row.audioTags.markers ?? MARKER_VOCABULARY[knobKey(row.modelId)],
  );
  const first = lists[0];
  if (!first || lists.some(list => !list)) return [];
  return first.filter(candidate =>
    lists.every(list =>
      list?.some(m => markerKey(m.tag) === markerKey(candidate.tag)),
    ),
  );
}

/** One guidance link per model that has one, in run order, each model once. */
function foldGuidance(
  voices: readonly DirectionVoice[],
  byModelId: Map<string, DubModelCapabilityDTO>,
): DirectionGuidance[] {
  const links: DirectionGuidance[] = [];
  const seen = new Set<string>();
  for (const voice of voices) {
    if (!voice.modelId) continue;
    const key = knobKey(voice.modelId);
    if (seen.has(key)) continue;
    seen.add(key);
    const url = MODEL_GUIDANCE[key];
    if (!url) continue;
    links.push({
      modelLabel:
        voice.modelLabel ??
        byModelId.get(voice.modelId)?.modelId ??
        voice.modelId,
      url,
    });
  }
  return links;
}

/** The distinct capability rows the run's voices actually resolve to. */
function distinctRowsFor(
  voices: readonly DirectionVoice[],
  byModelId: Map<string, DubModelCapabilityDTO>,
): DubModelCapabilityDTO[] {
  const rows: DubModelCapabilityDTO[] = [];
  const seen = new Set<string>();
  for (const voice of voices) {
    if (!voice.modelId) continue;
    const row = byModelId.get(voice.modelId);
    if (!row || seen.has(row.modelId)) continue;
    seen.add(row.modelId);
    rows.push(row);
  }
  return rows;
}

/**
 * Fold every model's declared controls into the one set the panel may draw.
 *
 * Kept only when EVERY model in the run names the control, because the run
 * sends ONE direction and the backend checks it against each model in turn.
 *
 * For a number, the range is the narrowest all of them accept — the highest of
 * the lowest values and the lowest of the highest — so a value the panel allows
 * is a value every model in the run allows. Models that disagree about a
 * control's TYPE drop it: there is no honest single field for it.
 *
 * A control no model declares limits for is reported by name instead, never
 * drawn with a range nobody stated.
 */
function foldKnobs(rows: readonly DubModelCapabilityDTO[]): {
  knobs: DirectionKnob[];
  undeclaredKnobs: string[];
} {
  const first = rows[0];
  if (!first) return { knobs: [], undeclaredKnobs: [] };
  // Order follows the first model's list, so the panel's fields do not move
  // about when the run's second voice changes.
  const shared = first.voiceSettings.knobs.filter(name =>
    rows.every(row => row.voiceSettings.knobs.includes(name)),
  );

  const knobs: DirectionKnob[] = [];
  const undeclaredKnobs: string[] = [];
  for (const name of shared) {
    const declared: DubKnobDeclarationDTO[] = [];
    for (const row of rows) {
      const one = row.voiceSettings.knobDeclarations?.[name];
      if (one) declared.push(one);
    }
    if (declared.length !== rows.length) {
      undeclaredKnobs.push(name);
      continue;
    }
    const numbers = declared.filter(isNumberDeclaration);
    const booleans = declared.filter(isBooleanDeclaration);
    if (numbers.length === declared.length) {
      const min = Math.max(...numbers.map(d => d.min));
      const max = Math.min(...numbers.map(d => d.max));
      if (min > max) {
        // The models' ranges do not overlap at all: no value satisfies both.
        undeclaredKnobs.push(name);
        continue;
      }
      const stated = numbers.find(d => typeof d.default === 'number')?.default;
      const fallback = stated ?? min;
      const presentation = declaredWords(declared);
      knobs.push({
        name,
        type: 'number',
        min,
        max,
        default: Math.min(Math.max(fallback, min), max),
        ...(presentation ? { presentation } : {}),
      });
    } else if (booleans.length === declared.length) {
      const presentation = declaredWords(declared);
      knobs.push({
        name,
        type: 'boolean',
        default:
          booleans.find(d => typeof d.default === 'boolean')?.default ?? false,
        ...(presentation ? { presentation } : {}),
      });
    } else {
      // One model calls it a number and another an on/off switch. There is no
      // single field that is honest for both, so it is named, not drawn.
      undeclaredKnobs.push(name);
    }
  }
  return { knobs, undeclaredKnobs };
}

/**
 * The words the route declared for a control, from the first model in the run
 * that carries a label and a sentence (PLAT-12030). The ends come along when
 * that model states them. `undefined` when no model does, so the local table
 * stands in.
 */
function declaredWords(
  declared: readonly DubKnobDeclarationDTO[],
): KnobPresentation | undefined {
  const worded = declared.find(d => d.label && d.effect);
  if (!worded?.label || !worded.effect) return undefined;
  return {
    label: worded.label,
    ...(worded.ends ? { ends: worded.ends } : {}),
    effect: worded.effect,
  };
}

const isNumberDeclaration = (
  declaration: DubKnobDeclarationDTO,
): declaration is Extract<DubKnobDeclarationDTO, { type: 'number' }> =>
  declaration.type === 'number';

const isBooleanDeclaration = (
  declaration: DubKnobDeclarationDTO,
): declaration is Extract<DubKnobDeclarationDTO, { type: 'boolean' }> =>
  declaration.type === 'boolean';

/** What the panel shows before anyone touches it and there is no previous run. */
export function defaultKnobValues(
  knobs: readonly DirectionKnob[],
): Record<string, number | boolean> {
  const values: Record<string, number | boolean> = {};
  for (const knob of knobs) values[knob.name] = knob.default;
  return values;
}

/**
 * The controls whose current value is outside what every model in the run
 * accepts, by name and in panel order.
 *
 * The panel refuses to send while this is not empty. The alternative — quietly
 * pulling the value back inside the range — would send something other than
 * what the person set, which is the one thing a direction must never do.
 */
export function knobValuesOutOfRange(
  knobs: readonly DirectionKnob[],
  values: Readonly<Record<string, number | boolean>>,
): string[] {
  const bad: string[] = [];
  for (const knob of knobs) {
    const value = values[knob.name];
    if (value === undefined) continue;
    if (knob.type === 'number') {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        bad.push(knob.name);
      } else if (value < knob.min || value > knob.max) {
        bad.push(knob.name);
      }
    } else if (typeof value !== 'boolean') {
      bad.push(knob.name);
    }
  }
  return bad;
}

/**
 * The direction the controls panel would send, or `undefined` when it would
 * send nothing.
 *
 * The comparison is against the MODEL's own declared values, never against
 * whatever the panel happened to open at. That matters because the panel opens
 * at the PREVIOUS run's values: a person who sees those values and starts the
 * run is asking for those values, and sending nothing would quietly give them
 * a different delivery from the one on screen.
 *
 * So nothing is sent only when every control sits exactly where the model
 * itself would leave it — which is, precisely, asking for nothing.
 *
 * Anything out of range sends nothing either. The caller is expected to have
 * held the run already; this is the second guard.
 */
export function settingsDirectionFrom(
  knobs: readonly DirectionKnob[],
  values: Readonly<Record<string, number | boolean>>,
): DubDirection | undefined {
  if (knobs.length === 0) return undefined;
  if (knobValuesOutOfRange(knobs, values).length > 0) return undefined;
  const modelDefaults = defaultKnobValues(knobs);
  const changed = knobs.some(
    knob => values[knob.name] !== modelDefaults[knob.name],
  );
  if (!changed) return undefined;
  const settings: Record<string, number | string | boolean> = {};
  for (const knob of knobs) {
    const value = values[knob.name];
    if (value !== undefined) settings[knob.name] = value;
  }
  return Object.keys(settings).length > 0
    ? { kind: 'config', settings }
    : undefined;
}
